import { useCallback, useEffect, useState, useRef } from "react";
import debounce from "lodash/debounce";
import {
  StepperContentHeaderDiv,
  FlexColumnDiv,
  FlexRowDiv,
  StepperContentBodyDiv,
} from "../../styledComponents/Components.styles";
import { textHints, textExamples } from "../../../utilities/hintText";
import { currentSRPSchemaVersion, JFROG_API_SRP_JSON_REPO_NAME } from "../../../utilities/constants";
import { searchKeyPerson } from "../../../utilities/utilityFunctions";
import { validateSRPInformationValue, validateSRPNameValue } from "../../../utilities/formValidation";
import { components } from 'react-select';
import { useDataContext } from "../../DataProvider/DataContext";
import Version from "../../../utilities/Version";
import { TextField } from "../formFields/TextField";
import { AsyncSelectField } from "../formFields/AsyncSelectField";
import LabelField from "../formFields/LabelField";

// Custom MultiValueRemove component for react-select
const MultiValueRemove = (props) => {
  props.innerProps.id = props.innerProps["aria-label"];
  return (
    <components.MultiValueRemove {...props}/>
  );
};

const SRPInformation = ({
  textFieldValues, 
  setTextFieldValues, 
  textFieldErrors,
  setTextFieldErrors, 
  selectedKeyPersonOption,
  selectedResponsibleOptions,
  setSelectedResponsibleOptions, 
  setSelectedKeyPersonOption,
  moduleInformation,
  setProjectKey,
  setModuleName,

}) => {

    const {
      ECUData,
      api,
      dynamicSelectOptionsMap
    } = useDataContext();

    // Initialize Version object only once, then update it when needed
    const srpdVersionObject = useRef(null);
    
    // Initialize the version object only if it doesn't exist
    if (!srpdVersionObject.current) {
      srpdVersionObject.current = new Version(textFieldValues.srpdVersion || "1.0.0");
    }

    const handleSRPAssigneeChange = (selectedOption) => {
        setSelectedKeyPersonOption(selectedOption);
        const newTextFieldValues = structuredClone(textFieldValues);
        const value = selectedOption?.value ?? "";
        newTextFieldValues.srpAssignee =  value.endsWith("@ford.com") ? value.split("@")[0] : value;
        setTextFieldValues(newTextFieldValues);
        validateSRPInformationValue("srpAssignee", 
          value, 
          setTextFieldErrors);
    };
    
    const handleTextInput = (e) => {
      const id = e.target.id;
      let value = e.target.value;
      if (id !== "fixVersion") {
        value = value.replace(/ +/g, '');
      }
      const newTextFieldValues = { ...textFieldValues };
  
      newTextFieldValues[id] = value;
      setTextFieldValues(newTextFieldValues);
  
      const isRequired = e.target.required;
      if (isRequired) {
        validateSRPInformationValue(id, 
          value, setTextFieldErrors);
  
        if(id === "srpdName"){
          validateSRPNameValue({value, moduleName: moduleInformation.moduleName, vehicleProgramOptions: dynamicSelectOptionsMap.vehiclePrograms  });
        }

        if(id === 'srpdVersion'){
          srpdVersionObject.current.version = value;
        }
      }
    };

    const handleKeyParticipantsChange = (selectedOptions) => {
  
      setSelectedResponsibleOptions(selectedOptions);
  
      const newTextFieldValues = structuredClone(textFieldValues);
      newTextFieldValues.srpWatchers = selectedOptions.map(
        (option) => option.value.endsWith("@ford.com")
          ? option.value.split("@")[0]
          : option.value
      );
      setTextFieldValues(newTextFieldValues);
  };

    // https://github.com/JedWatson/react-select/issues/614
    // Implements debouncing with lodash.debounce to drastically lower the number of API calls when entering a name in keyContacts
    const getOptions = useCallback(
        debounce((userInput, callback) => {
        searchKeyPerson({
            api: api,
            inputValue: userInput
        }).then(options => {
            callback(options);
        });
        }, 1000),
        []
    );

    const [srpFileName, setSRPFileName] = useState("");

    useEffect(() => {
      const srpdName = textFieldValues.srpdName;
      const moduleName = moduleInformation.moduleName;

      if (srpdName) {
        const srpdNameParts = srpdName.split("_");
        const projectKey = srpdNameParts.length > 0 ? srpdNameParts[0] : "";
        setProjectKey(projectKey);
      }

      if (moduleName) {
        setModuleName(moduleName);
      } 
    }
    , [textFieldValues.srpdName, moduleInformation.moduleName]);

    useEffect(() => {
      const srpdName = textFieldValues.srpdName;
      const srpdVersion = textFieldValues.srpdVersion;

      if (srpdName && srpdVersion) {
        setSRPFileName(`${srpdName}_${srpdVersion}SRPd.json`);
      }
    }, [textFieldValues.srpdName, textFieldValues.srpdVersion]);

    const shouldDisplaySRPdFileNameAndDestination = textFieldValues.srpdName !== "" && textFieldValues.srpdVersion !== "" && textFieldValues.srpdVersion !== undefined; 

    return(
        <FlexColumnDiv style={{marginLeft: '40px', marginBottom: '20px'}}>
          <StepperContentHeaderDiv style={{paddingTop: '10px', paddingBottom: '10px', marginBottom: '20px', gap: '80px'}}>
            <FlexColumnDiv style={{gap: '10px'}}>
              <LabelField
                label="SRPd Name"
                value={textFieldValues.srpdName}
              />
              <LabelField
                label="SRP Schema Version"
                value={currentSRPSchemaVersion}
              />
            </FlexColumnDiv>
            <FlexColumnDiv style={{gap: '10px'}}>
              <LabelField 
                label="SRPd file name:"
                value={shouldDisplaySRPdFileNameAndDestination ? srpFileName : "N/A"}
              />
              <LabelField
                label="JFROG repository destination:"
                value={shouldDisplaySRPdFileNameAndDestination ? JFROG_API_SRP_JSON_REPO_NAME : "N/A"}
              />
            </FlexColumnDiv>


          </StepperContentHeaderDiv>



          <StepperContentBodyDiv style={{width: '1240px', alignSelf: 'center', marginBottom: '20px', alignItems: 'center', flexDirection: 'column'}}>
              <FlexRowDiv style={{gap: '36px'}}>
              <TextField
                label="SRPd Version"
                value={textFieldValues.srpdVersion}
                required={true}
                placeholder="Enter version"
                onChange={handleTextInput}
                textHint={textHints.version}
                errorMessage={textFieldErrors.srpdVersionValidationError}
                idLabel="srpdVersion"
                idInput="srpdVersion"
                idError="srpdVersion_error"
                nameInput="srpdVersionInput"
                pathToField="srpdVersion"
                stylingVariant="body"
                isPrefilledValueChanged={textFieldValues.srpdVersion !== ECUData.current.prefilledJSONContent.srpdVersion}
              />
              <div style={{width: '560px'}}></div>
            </FlexRowDiv>
            
            <FlexRowDiv style={{gap: '36px'}}>
              <AsyncSelectField
                required={true}
                label="Select Key Person"
                value={selectedKeyPersonOption}
                placeholder="Enter key person"
                onChange={handleSRPAssigneeChange}
                onFocus={() => {
                  setTextFieldValues((prev => ({...prev, srpAssignee: ""})));
                  setSelectedKeyPersonOption(null);
                }}
                textExample={textExamples.srpAssignee}
                textHint={textHints.srpAssignee}
                errorMessage={textFieldErrors.srpAssigneeValidationError}
                overridenErrorMessage={"You must choose a value"}
                isMulti={false}
                loadOptions={getOptions}
                idLabel="srpAssignee"
                idInput="srpAssignee"
                idError="srpAssignee_error"
                nameInput="srpAssignee"
                pathToField="srpAssignee"
                isPrefilledValueChanged={textFieldValues.srpAssignee !== ECUData.current.prefilledJSONContent.srpAssignee}
                stylingVariant="body"
              />
              <AsyncSelectField
                components={{ MultiValueRemove }}
                label="Participants of SRP"
                value={selectedResponsibleOptions}
                placeholder="Enter participants"
                onChange={handleKeyParticipantsChange}
                textExample={textExamples.srpWatchers}
                textHint={"Select all participants that should be notified of changes to this SRP"}
                errorMessage={textFieldErrors.srpWatchers}
                overridenErrorMessage={"You must choose a value"}
                // isLoading={keyContactsIsLoading}
                isMulti={true}
                loadOptions={getOptions}
                idLabel="srpWatchers"
                idInput="srpWatchers"
                idError="srpWatchers_error"
                nameInput="srpWatchers"
                pathToField="srpWatchers"
                isPrefilledValueChanged={JSON.stringify(textFieldValues.srpWatchers) !== JSON.stringify(ECUData.current.prefilledJSONContent.srpWatchers)}
                stylingVariant="body"
              />
            </FlexRowDiv>
                        <FlexRowDiv style={{gap: '36px'}}>
              <TextField
                label="Release Work Item Key"
                value={textFieldValues.releaseObjectKey}
                required={true}
                placeholder="Enter Release Work Item Key"
                onChange={handleTextInput}
                textHint={textHints.releaseObjectKey}
                textExample={textExamples.releaseObjectKey}
                errorMessage={textFieldErrors.releaseObjectKeyValidationError}
                idLabel="releaseObjectKey"
                idInput="releaseObjectKey"
                idError="releaseObjectKey_error"
                nameInput="releaseObjectKey"
                pathToField="releaseObjectKey"
                stylingVariant="body"
                isPrefilledValueChanged={textFieldValues.releaseObjectKey !== ECUData.current.prefilledJSONContent.releaseObjectKey}
              />
              <TextField
                label="Release Work Item Fix Version"
                value={textFieldValues.fixVersion}
                required={true}
                placeholder="Enter Release Work Item Fix Version"
                onChange={handleTextInput}
                textHint={textHints.fixVersion}
                textExample={textExamples.fixVersion}
                errorMessage={textFieldErrors.fixVersionValidationError}
                idLabel="fixVersion"
                idInput="fixVersion"
                idError="fixVersion_error"
                nameInput="fixVersion"
                pathToField="fixVersion"
                stylingVariant="body"
                isPrefilledValueChanged={textFieldValues.fixVersion !== ECUData.current.prefilledJSONContent.fixVersion}
              />
              </FlexRowDiv>
        </StepperContentBodyDiv>
          </FlexColumnDiv>
    );
}

export default SRPInformation;